// CalendarComponent.vue
<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { Calendar } from 'v-calendar';
import 'v-calendar/style.css';

// 인터페이스 정의
interface CalendarProps {
  modelValue: Date | null;
  isVisible: boolean;
}

// 속성 정의
const props = defineProps<CalendarProps>();
const emit = defineEmits(['update:modelValue', 'update:isVisible', 'selectDate', 'close']);

// 선택된 날짜 (임시 선택용)
const selectedDate = ref(props.modelValue);
// 실제 선택된 날짜 (하이라이트용)
const highlightedDate = ref<Date | null>(null);

// props.modelValue 변경 시 selectedDate 업데이트
watch(
  () => props.modelValue,
  newValue => {
    if (newValue) {
      selectedDate.value = new Date(newValue);
      highlightedDate.value = new Date(newValue);
    }
  },
);

// 모달 외부 클릭 시 닫기 처리
const handleOutsideClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (props.isVisible && !target.closest('.calendar-container') && !target.closest('.vc-popover-content')) {
    emit('update:isVisible', false);
  }
};

// 날짜 선택 처리 (클릭 시 즉시 선택 & 닫기)
const handleDateSelect = (dayInfo: any) => {
  // v-calendar의 dayclick 이벤트는 { date: Date } 형태의 객체를 전달
  let selectedDateValue: Date;

  if (dayInfo && dayInfo.date instanceof Date) {
    selectedDateValue = dayInfo.date;
  } else if (dayInfo instanceof Date) {
    selectedDateValue = dayInfo;
  } else {
    console.error('지원되지 않는 날짜 형식:', dayInfo);
    return;
  }

  selectedDate.value = selectedDateValue;
  highlightedDate.value = selectedDateValue;
  emit('update:modelValue', selectedDateValue);
  emit('selectDate', selectedDateValue);
  emit('update:isVisible', false);
};

// 닫기 버튼 클릭 처리
const close = () => {
  emit('update:isVisible', false);
  emit('close');
};

// 컴포넌트 마운트 시 이벤트 리스너 등록
onMounted(() => {
  document.addEventListener('mousedown', handleOutsideClick);
});

// 컴포넌트 언마운트 전 이벤트 리스너 제거
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleOutsideClick);
});
</script>

<template>
  <div
    v-if="isVisible"
    class="calendar-container bg-white rounded-lg shadow-lg w-[370px] overflow-hidden z-10 relative border-[2px] border-primary-500 px-[20px] py-[10px]"
  >
    <!-- 캘린더 내용 -->
    <Calendar
      v-model="selectedDate"
      :first-day-of-week="1"
      locale="ko-KR"
      :masks="{
        title: 'YYYY년 M월',
        weekdays: 'WW',
      }"
      @dayclick="handleDateSelect"
      trim-weeks
      :min-weeks="4"
      :max-weeks="6"
      :attributes="
        highlightedDate
          ? [{ highlight: { color: '#FEF3C7' }, dates: [highlightedDate] }]
          : []
      "
    />

    <!-- 버튼 영역 (닫기만) -->
    <div class="flex border-t border-[#E9E9E9] mt-2 pt-2">
      <button @click="close" class="flex-1 py-3 text-white font-bold bg-primary-500 hover:bg-[#E87A32] rounded-lg">
        닫기
      </button>
    </div>
  </div>
</template>

<style scoped>
/* VCalendar 커스텀 스타일 */
:deep(.vc-container) {
  --vc-font-family: 'NanumSquare', sans-serif;
  --vc-text-base-color: #404040;
  --vc-day-content-color: #404040;
  --vc-accent-color: #f97316;
  --vc-border-color: #e9e9e9;
  font-family: 'NanumSquare', sans-serif;
  border: none;
  width: 100%;
}

/* vc-attr 관련 스타일 추가 */
:deep(.vc-attr) {
  --vc-accent-color: #f97316 !important;
}

:deep(.vc-highlight) {
  background-color: #f97316 !important;
  color: #404040 !important;
}

:deep(.vc-header) {
  padding: 0 0 10px 0;
}

:deep(.vc-title) {
  font-weight: bold;
  font-size: 16px;
  color: #404040;
}

:deep(.vc-arrow) {
  color: #404040;
}

:deep(.vc-weekday) {
  color: #404040;
  font-size: 14px;
  font-weight: normal;
  margin-top: 5px;
}

:deep(.vc-day) {
  height: 30px;
  width: 30px;
}

:deep(.vc-day-content) {
  font-size: 12px;
  height: 32px;
  width: 32px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.vc-day-content:hover) {
  background-color: rgba(254, 243, 199, 0.3);
  border-radius: 9999px;
}

:deep(.vc-highlight) {
  border-radius: 9999px;
  /* background-color: #fef3c7 !important; */
}

:deep(.vc-day.is-not-in-month) {
  opacity: 0.4;
}
</style>
